import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { UserCog, UserPlus, ShieldOff, Loader, Search } from "lucide-react";

export default function AdminManagementPanel({ currentUserId }) {
  const [admins, setAdmins] = useState([]);
  const [users, setUsers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [showPromote, setShowPromote] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [promoteSearch, setPromoteSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [a, u] = await Promise.all([api.get("/admin/admins"), api.get("/admin/users")]);
      setAdmins(a.data);
      setUsers(u.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.post("/admin/admins", inviteForm);
      setMsg(`Admin '${inviteForm.name}' created. Share the login link + password securely (out of band).`);
      setInviteForm({ name: "", email: "", phone: "", password: "" });
      setShowInvite(false);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(""), 8000);
    }
  };

  const promote = async (userId, name) => {
    if (!window.confirm(`Promote ${name} to admin? They will gain full admin access.`)) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.post("/admin/admins/promote", { user_id: userId });
      setMsg(`${name} is now an admin.`);
      setShowPromote(false);
      setPromoteSearch("");
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(""), 6000);
    }
  };

  const revoke = async (adminId, email) => {
    if (!window.confirm(`Revoke admin access for ${email}? They will be demoted to customer.`)) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.post(`/admin/admins/${adminId}/revoke`);
      setMsg(`${email} demoted to customer.`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(""), 6000);
    }
  };

  const promotable = users.filter((u) => u.role !== "admin").filter((u) =>
    !promoteSearch ||
    (u.name || "").toLowerCase().includes(promoteSearch.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(promoteSearch.toLowerCase())
  );

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="admin-management-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <UserCog className="text-asap-orange" size={20}/>
          <h3 className="font-display uppercase text-asap-deep">Manage Admins ({admins.length})</h3>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setShowInvite(!showInvite); setShowPromote(false); }} data-testid="show-invite-btn" className="px-3 py-1.5 bg-asap-yellow text-asap-deep font-bold text-xs uppercase tracking-wider border-2 border-asap-deep inline-flex items-center gap-1">
            <UserPlus size={12}/> Invite New Admin
          </button>
          <button onClick={() => { setShowPromote(!showPromote); setShowInvite(false); }} data-testid="show-promote-btn" className="px-3 py-1.5 bg-white text-asap-deep font-bold text-xs uppercase tracking-wider border-2 border-asap-deep inline-flex items-center gap-1 hover:bg-asap-deep hover:text-white">
            Promote Existing
          </button>
        </div>
      </div>

      {msg && <div className="bg-emerald-50 border-2 border-emerald-500 text-emerald-800 px-4 py-2 font-bold text-sm mb-4" data-testid="admin-mgmt-msg">{msg}</div>}
      {err && <div className="bg-red-50 border-2 border-asap-red text-asap-red px-4 py-2 font-bold text-sm mb-4">{err}</div>}

      {showInvite && (
        <div className="mb-4 p-4 border-2 border-asap-yellow bg-asap-yellow/10" data-testid="invite-form">
          <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-3">Create Brand-New Admin Account</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input placeholder="Full name (e.g. Jane, Dispatcher)" value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} data-testid="invite-name" className="px-3 py-2 border-2 border-asap-deep text-sm"/>
            <input placeholder="Email address" type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} data-testid="invite-email" className="px-3 py-2 border-2 border-asap-deep text-sm"/>
            <input placeholder="Phone (optional)" value={inviteForm.phone} onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })} data-testid="invite-phone" className="px-3 py-2 border-2 border-asap-deep text-sm"/>
            <input placeholder="Temp password (min 8)" type="text" value={inviteForm.password} onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })} data-testid="invite-password" className="px-3 py-2 border-2 border-asap-deep text-sm"/>
          </div>
          <div className="mt-3 flex gap-2 items-center">
            <button onClick={invite} disabled={busy || !inviteForm.name || !inviteForm.email || (inviteForm.password || "").length < 8} data-testid="invite-submit" className="px-4 py-2 bg-asap-yellow text-asap-deep font-display uppercase text-xs border-2 border-asap-deep disabled:opacity-50 inline-flex items-center gap-1">
              {busy ? <Loader size={12} className="animate-spin"/> : <UserPlus size={12}/>} Create Admin
            </button>
            <div className="text-[11px] text-slate-500">Password stays hidden — share it privately with the new admin.</div>
          </div>
        </div>
      )}

      {showPromote && (
        <div className="mb-4 p-4 border-2 border-asap-deep bg-slate-50" data-testid="promote-form">
          <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-3">Promote Existing User to Admin</div>
          <div className="flex items-center gap-2 mb-3">
            <Search size={14} className="text-slate-500"/>
            <input placeholder="Search by name or email" value={promoteSearch} onChange={(e) => setPromoteSearch(e.target.value)} data-testid="promote-search" className="flex-1 px-3 py-2 border-2 border-asap-deep text-sm"/>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {promotable.slice(0, 20).map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-2 px-3 py-2 bg-white border border-slate-200" data-testid={`promote-row-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-asap-deep text-sm truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email} · {u.role}</div>
                </div>
                <button onClick={() => promote(u.id, u.name)} disabled={busy} data-testid={`promote-btn-${u.id}`} className="px-2 py-1 bg-asap-yellow text-asap-deep font-bold text-[10px] uppercase tracking-wider border-2 border-asap-deep disabled:opacity-50 shrink-0">
                  Promote →
                </button>
              </div>
            ))}
            {promotable.length === 0 && <div className="text-xs text-slate-500 text-center py-4">No users match your search.</div>}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admins-table">
          <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
            <tr><th className="py-2">Name</th><th>Email</th><th>Phone</th><th>Origin</th><th>Since</th><th></th></tr>
          </thead>
          <tbody>
            {admins.map((a) => {
              const isSelf = a.id === currentUserId;
              const origin = a.invited_by ? "Invited" : a.promoted_by ? "Promoted" : "Seeded";
              return (
                <tr key={a.id} className="border-b last:border-0" data-testid={`admin-row-${a.id}`}>
                  <td className="py-2 font-bold text-asap-deep">{a.name} {isSelf && <span className="text-[10px] text-asap-orange">(you)</span>}</td>
                  <td className="text-slate-600">{a.email}</td>
                  <td className="text-slate-600">{a.phone || "—"}</td>
                  <td><span className="text-[10px] uppercase px-2 py-0.5 border font-bold border-asap-deep text-asap-deep">{origin}</span></td>
                  <td className="text-xs text-slate-500">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                  <td>
                    {!isSelf && origin !== "Seeded" && (
                      <button onClick={() => revoke(a.id, a.email)} disabled={busy} data-testid={`revoke-${a.id}`} className="px-2 py-1 bg-asap-red text-white text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1 disabled:opacity-50">
                        <ShieldOff size={10}/> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
